import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useListFoods } from "@workspace/api-client-react";

type TranslatableFood = {
  foodName: string;
  translations?: Record<string, string> | string | null;
  userId?: number | null;
};

export function useTranslatedFoodName() {
  const { i18n } = useTranslation();
  const locale = i18n.language || "en";

  const { data: allFoods } = useListFoods({ locale: locale !== "en" ? locale : undefined });

  const translationsByName = useMemo(() => {
    const map = new Map<string, string>();
    if (locale === "en" || !allFoods) return map;
    const lang = locale.split("-")[0];
    for (const food of allFoods) {
      if ((food as TranslatableFood).userId != null || !(food as TranslatableFood).translations) continue;
      try {
        const raw = (food as TranslatableFood).translations;
        const t: Record<string, string> = typeof raw === "string" ? JSON.parse(raw) : (raw ?? {});
        const translated = t[lang] || t[locale];
        if (translated) {
          map.set(food.foodName.toLowerCase(), translated);
        }
      } catch {}
    }
    return map;
  }, [allFoods, locale]);

  const getTranslatedName = useCallback(
    (food: TranslatableFood) => {
      if (locale === "en") return food.foodName;
      if (food.userId != null) return food.foodName;

      if (food.translations) {
        try {
          const map: Record<string, string> =
            typeof food.translations === "string"
              ? JSON.parse(food.translations)
              : food.translations;
          const lang = locale.split("-")[0];
          return map[lang] || map[locale] || food.foodName;
        } catch {
          return food.foodName;
        }
      }

      const lookup = translationsByName.get(food.foodName.toLowerCase());
      return lookup || food.foodName;
    },
    [locale, translationsByName],
  );

  const translateFoodName = useCallback(
    (name: string) => {
      if (locale === "en") return name;
      return translationsByName.get(name.toLowerCase()) || name;
    },
    [locale, translationsByName],
  );

  return { getTranslatedName, translateFoodName, locale };
}

export function getTranslatedFoodNameFromData(
  foodName: string,
  translations: Record<string, string> | string | null | undefined,
  locale: string,
  userId?: number | null,
): string {
  if (locale === "en") return foodName;
  if (userId != null) return foodName;
  if (!translations) return foodName;

  try {
    const map: Record<string, string> =
      typeof translations === "string" ? JSON.parse(translations) : translations;
    const lang = locale.split("-")[0];
    return map[lang] || map[locale] || foodName;
  } catch {
    return foodName;
  }
}
